import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Building2, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useJobs } from "@/hooks/useJobs";
import heroMinimal from "@/assets/hero-minimal.jpg";
export const HeroSection = () => {
  const navigate = useNavigate();
  const {
    allJobs
  } = useJobs({});
  const scrollToSection = (className: string) => {
    const section = document.querySelector(className);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden w-full" aria-label="Hero-Bereich">
      {/* Enhanced Organic Background with Depth */}
      <div className="absolute inset-0 w-full">
        <img src={heroMinimal} alt="Elegante Hospitality-Atmosphäre" className="absolute inset-0 w-full h-full object-cover opacity-10 sm:opacity-15" />
        <div className="absolute inset-0 bg-gradient-organic" />
        
        {/* Layered Background Elements */}
        <div className="absolute top-1/4 right-1/4 w-32 h-32 sm:w-96 sm:h-96 rounded-full blur-3xl opacity-40 sm:opacity-60" style={{
        background: 'radial-gradient(circle, hsl(210 65% 32% / 0.08) 0%, transparent 70%)'
      }} />
        <div className="absolute bottom-1/3 left-1/3 w-24 h-24 sm:w-64 sm:h-64 rounded-full blur-2xl opacity-30 sm:opacity-40" style={{
        background: 'radial-gradient(circle, hsl(210 65% 32% / 0.06) 0%, transparent 70%)'
      }} />
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.01] sm:opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, hsl(210 65% 32%) 1px, transparent 0)`,
        backgroundSize: '30px 30px'
      }} />
      </div>
      
      {/* Hero Content */}
      <div className="relative z-10 w-full pt-24 pb-8 sm:pt-32 sm:pb-16 max-w-full">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          {/* Main Header */}
          <div className="text-center mb-8 sm:mb-12 animate-fade-in">
            <p className="font-medium tracking-wider uppercase text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 px-4 sm:px-0" style={{
            color: 'hsl(210 65% 32%)'
          }}>
              SCHOLZ INTERIM – SEIT 2002 FÜR DIE HOSPITALITY
            </p>
            
            <div className="flex items-center justify-center space-x-2.5 sm:space-x-4 mb-3 sm:mb-5">
              <span className="w-8 sm:w-12 md:w-16 h-px bg-gradient-to-r from-transparent via-primary to-primary"></span>
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{
              backgroundColor: 'hsl(210 65% 32%)'
            }}></div>
              <span className="w-8 sm:w-12 md:w-16 h-px bg-gradient-to-l from-transparent via-primary to-primary"></span>
            </div>
            
            <h1 className="text-4xl sm:text-6xl font-display font-extralight bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-transparent mb-2 sm:mb-3 leading-[1.15] tracking-tight">
              Headhunting
              <span className="block font-light italic mt-1.5 sm:mt-2 text-2xl sm:text-4xl" style={{
              color: 'hsl(210 65% 32%)'
            }}>
                Hotellerie & Gastronomie
              </span>
            </h1>
            <h2 className="text-base sm:text-lg font-light text-muted-foreground mt-3 sm:mt-4 max-w-3xl mx-auto px-4">
              Personalvermittlung für Führungspositionen mit über 20 Jahren Branchenerfahrung
            </h2>
          </div>

          {/* Split Hero Cards */}
          <div className="grid md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 max-w-6xl mx-auto mb-8 w-full">
            {/* For Candidates */}
            <div className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-primary/20 bg-gradient-to-br from-background/95 via-background/90 to-background/95 backdrop-blur-xl p-5 sm:p-6 md:p-8 shadow-2xl hover:shadow-primary/10 transition-all duration-500 hover:scale-[1.02] cursor-pointer" onClick={() => scrollToSection('.hero-ai-job-search')}>
              {/* Decorative gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
              <h2 className="text-xl sm:text-3xl font-display font-light mb-3 sm:mb-4 leading-[1.3]">
                Sie suchen den
                <span className="block font-medium mt-0.5" style={{
                color: 'hsl(210 65% 32%)'
              }}>nächsten Job?</span>
              </h2>
                
                <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-7 md:mb-8 text-muted-foreground text-sm sm:text-base">
                  <li className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>{allJobs.length} aktuelle Stellenangebote</span>
                  </li>
                  <li className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Bundesweite Führungspositionen</span>
                  </li>
                  <li className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Aktive Begleitung bei der Suche</span>
                  </li>
                </ul>
                
                <Button size="default" className="gap-2 group/btn w-auto" onClick={e => {
                e.stopPropagation();
                navigate('/bewerbende');
              }}>
                  Offene Stellen
                  <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>

            {/* For Companies */}
            <div className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-primary/20 bg-gradient-to-br from-background/95 via-background/90 to-background/95 backdrop-blur-xl p-5 sm:p-6 md:p-8 shadow-2xl hover:shadow-primary/10 transition-all duration-500 hover:scale-[1.02] cursor-pointer" onClick={() => scrollToSection('#expertise')}>
              {/* Decorative gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <h2 className="text-xl sm:text-3xl font-display font-light mb-3 sm:mb-4 leading-[1.3]">
                  Sie suchen <span className="font-medium" style={{
                  color: 'hsl(210 65% 32%)'
                }}>passende Führungskräfte?</span>
                </h2>
                
                <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-7 md:mb-8 text-muted-foreground text-sm sm:text-base">
                  <li className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>General Manager & Operations Director</span>
                  </li>
                  <li className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Executive Chef & F&B Director</span>
                  </li>
                  <li className="flex items-start gap-2.5 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Revenue & Commercial Director</span>
                  </li>
                </ul>
                
                <Button size="default" variant="outline" className="gap-2 group/btn w-auto" onClick={e => {
                e.stopPropagation();
                scrollToSection('#expertise');
              }}>
                  Services entdecken
                  <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          
        </div>
      </div>

      {/* Enhanced Organic Bottom Shape */}
      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-32 bg-background organic-shape">
        <div className="absolute inset-0 bg-gradient-accent-subtle opacity-50"></div>
      </div>
    </section>;
};